'use client';

import { useEffect, useState, ReactNode } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import preview from 'html-preview';
import { useTheme } from '@emotion/react';
import ArrowDownSVG from '../../public/img/keyboard_arrow_down.svg';
import TriangleDownSVG from '../../public/img/triangle-down.svg';
import ArrowUpSVG from '../../public/img/arrow-up.svg';
import { StyledText } from '../styled/typography';
import { DEVICE_SCREENS, DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { getTextPreview } from '../../utils';
import { Icon } from '../icon/icon';
import { Compilation, Social } from '../../models';
import { Image } from '../../models/image';
import { CompilationSlider } from '../compilation-slider/compilation-slider';

export function Collapsable({
  head,
  body,
}: {
  head: ReactNode;
  body: ReactNode;
}) {
  const [isCollapse, setIsCollapse] = useState(false);
  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onClick={() => setIsCollapse(!isCollapse)}>{head}</div>
      {isCollapse && <div>{body}</div>}
    </div>
  );
}

export function CollapsableRotate({
  head,
  body,
}: {
  head: string | ReactNode;
  body: string | ReactNode;
}) {
  const [isCollapse, setIsCollapse] = useState(false);
  return (
    <div>
      <button
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
        type='button'
        onClick={() => setIsCollapse(!isCollapse)}
      >
        <div css={{ paddingRight: 16 }}>{head}</div>
        <div>
          <TriangleDownSVG
            css={{
              width: 12,
              height: 12,
              fill: 'black',
              transform: isCollapse ? 'rotate(180deg)' : 'none',
            }}
          />
        </div>
      </button>
      {isCollapse && <div>{body}</div>}
    </div>
  );
}

export function BestPriceCollapsable({
  head,
  body,
}: {
  head: string | ReactNode;
  body: string | ReactNode;
}) {
  const [isCollapse, setIsCollapse] = useState(false);
  return (
    <div css={{ maxWidth: '80%', ...(!isCollapse && { display: 'flex' }) }}>
      <button
        css={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
        type='button'
        onClick={() => setIsCollapse(!isCollapse)}
      >
        <div css={{ paddingRight: 6 }}>{head}</div>
        <div
          css={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ArrowDownSVG
            css={{
              marginTop: isCollapse ? 0 : 2,
              width: 8,
              height: 6,
              fill: '#7D7B7A',
              transform: isCollapse ? 'rotate(180deg)' : 'none',
            }}
          />
        </div>
      </button>
      {isCollapse && <div css={{ marginTop: 4 }}>{body}</div>}
    </div>
  );
}

export function CollapsableRotateAnimate({
  head,
  body,
  isCollapse,
  callback,
}: {
  head: string | ReactNode;
  body: string | ReactNode;
  isCollapse: boolean;
  callback: () => void;
}) {
  return (
    <div>
      <button
        css={{
          display: 'inline-flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        type='button'
        onClick={callback}
      >
        <div css={{ paddingRight: 16 }}>{head}</div>
        <div>
          <TriangleDownSVG
            css={{
              width: 12,
              height: 12,
              fill: 'black',
              transform: isCollapse ? 'rotate(180deg)' : 'none',
              transition: '0.4s ease-in-out',
            }}
          />
        </div>
      </button>
      <div
        css={{
          transform: 'translateZ(0)',
          transition: '0.3s ease-in-out',
          display: 'block',
          maxHeight: isCollapse ? 560 : 0,
          overflow: 'auto',
          paddingLeft: 10,
          marginLeft: '-10px',
        }}
      >
        {body}
      </div>
    </div>
  );
}

export function LargeCollapsableRotate({
  head,
  body,
  isCollapse,
  callback,
}: {
  head: string | ReactNode;
  body: string | ReactNode;
  isCollapse: boolean;
  callback: () => void;
}) {
  return (
    <div
      css={{
        boxShadow: '0 1px 1px rgba(0, 0, 0, 0.05)',
      }}
    >
      <button
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          height: 75,
        }}
        type='button'
        onClick={callback}
      >
        <div>{head}</div>
        <div>
          <ArrowUpSVG
            css={{
              width: 14,
              height: 9,
              transform: isCollapse ? 'none' : 'rotate(180deg)',
              fill: '#C7C7CC',
            }}
          />
        </div>
      </button>
      <div
        css={{
          transform: 'translateZ(0)',
          transition: '0.3s ease-in-out',
          display: 'block',
          maxHeight: isCollapse ? 10000 : 0,
          overflow: 'hidden',
          paddingLeft: 10,
          marginLeft: '-10px',
        }}
      >
        {body}
      </div>
    </div>
  );
}

interface ImageCompilation extends Image {
  is_main: boolean;
}
export function CropDescriptionForCompilationPage({
  text,
  deskTopCount,
  mobileCount,
  images,
  compilation,
}: {
  text: string;
  deskTopCount: number;
  mobileCount: number;
  images: ImageCompilation[];
  compilation: Compilation;
}) {
  const [isAdaptive, setIsAdaptive] = useState(false);
  const [showReadMoreButton, setShowReadMore] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [textIsCollapsed, setTextIsCollapsed] = useState(false);

  useEffect(() => {
    if (window.innerWidth <= DEVICE_SCREENS.md) {
      setIsAdaptive(true);
      if (text.length > text.slice(0, mobileCount).length) {
        setShowReadMore(true);
        setTextIsCollapsed(true);
      } else if (
        images.length > 0 &&
        !(images.length === 1 && images[0].is_main)
      ) {
        setTextIsCollapsed(false);
        setShowReadMore(true);
      } else {
        setTextIsCollapsed(false);
        setShowReadMore(false);
      }
    } else {
      setIsAdaptive(false);
      if (text.length > text.slice(0, deskTopCount).length) {
        setShowReadMore(true);
        setTextIsCollapsed(true);
      } else if (
        images.length > 0 &&
        !(images.length === 1 && images[0].is_main)
      ) {
        setTextIsCollapsed(false);
        setShowReadMore(true);
      } else {
        setTextIsCollapsed(false);
        setShowReadMore(false);
      }
    }
  }, [text]);

  return (
    <>
      {images.length > 0 &&
        !(images.length === 1 && images[0].is_main) &&
        collapsed && (
          <CompilationSlider
            images={compilation.images}
            compilation={compilation}
          />
        )}
      {text.length > 0 && (
        <div
          css={{
            fontFamily: 'Golos',
            fontSize: 24,
            lineHeight: '150%',
            textAlign: 'center',
            padding: '0 10%',
            color: '#000000',
            display: 'block',
            fontWeight: 300,
            marginBottom: '2rem',
            [DEVICE_SCREENS_MAX_STRING.xl]: {
              fontSize: 20,
            },
            [DEVICE_SCREENS_MAX_STRING.lg]: {
              fontWeight: 800,
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              fontSize: 16,
              lineHeight: '140%',
              padding: '0 7%',
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              fontSize: 13,
              lineHeight: '140%',
              padding: '0 1%',
              fontWeight: 600,
            },
          }}
          dangerouslySetInnerHTML={{
            __html: !textIsCollapsed
              ? text
              : `${text.slice(0, isAdaptive ? mobileCount : deskTopCount)}...`,
          }}
        />
      )}
      {showReadMoreButton && (
        <div css={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={() => {
              setCollapsed((prev) => !prev);
              setTextIsCollapsed((prev) => !prev);
            }}
            css={{ borderBottom: '1px dashed #000000', cursor: 'pointer' }}
            type='button'
          >
            {collapsed ? 'Свернуть' : 'Читать далее'}
          </button>
        </div>
      )}
    </>
  );
}

export function CropText({
  text,
  deskTopCount,
  mobileCount,
}: {
  text: string | ReactNode;
  deskTopCount: number;
  mobileCount: number;
}) {
  const [isAdaptive, setIsAdaptive] = useState(false);
  const [showReadMoreButton, setShowReadMore] = useState(false);
  useEffect(() => {
    if (window.innerWidth <= DEVICE_SCREENS.md) {
      setIsAdaptive(true);
      if (preview(text || '', 9e9).length > preview(text, mobileCount).length) {
        setShowReadMore(true);
      } else {
        setShowReadMore(false);
      }
    } else {
      setIsAdaptive(false);
      if (
        preview(text || '', 9e9).length > preview(text, deskTopCount).length
      ) {
        setShowReadMore(true);
      } else {
        setShowReadMore(false);
      }
    }
  }, [text]);

  return (
    <StyledText className='crop-text'>
      <div
        dangerouslySetInnerHTML={{
          __html: showReadMoreButton
            ? getTextPreview(text, isAdaptive ? mobileCount : deskTopCount)
            : text,
        }}
      />
      {showReadMoreButton && (
        <button
          onClick={() => setShowReadMore(false)}
          css={{ borderBottom: '1px dashed #000000' }}
          type='button'
        >
          Полное описание
        </button>
      )}
    </StyledText>
  );
}

export function AskQuestion({ socials }: { socials: { results: Social[] } }) {
  const theme = useTheme();
  return (
    <div
      // social-question
      css={{
        [DEVICE_SCREENS_MAX_STRING.md]: {
          display: 'flex',
          justifyContent: 'center',
        },
      }}
    >
      <Collapsable
        head={
          <div
            // social-question__head
            css={{
              marginTop: 11,
              cursor: 'pointer',
              display: 'flex',
              color: theme.colors.text,
              marginBottom: '8px',
              fontFamily: 'Golos',
              fontSize: 14,
              fontWeight: 500,
              svg: { stroke: theme.colors.text },
              textDecoration: 'underline',
              textDecorationStyle: 'dashed',
            }}
          >
            ЗАДАТЬ ВОПРОС{' '}
            <div
              css={{
                display: 'flex',
                marginLeft: '9px',
                maxHeight: '20px',
                width: '10px',
              }}
            >
              <Icon iconId='angle-down' />
            </div>
          </div>
        }
        body={
          <div
            // social-question__body
            css={{
              svg: {
                width: '22px',
                height: '22px',
              },
              display: 'flex',
              flexWrap: 'wrap',
              'a:not(:last-child)': {
                marginRight: '12px',
                marginBottom: '6px',
              },
            }}
          >
            {socials.results.map((social) => (
              <a key={`Anchor__${social.id}`} href={social.url}>
                <Icon key={`Icon__${social.id}`} iconId={social.type} />
              </a>
            ))}
          </div>
        }
      />
    </div>
  );
}
